import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Col,
  Divider,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import { get, omit } from 'lodash';
import queryString from 'query-string';

import WarehouseServices from 'services/warehouse';

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';

import {
  ACTIVE_INACTIVE_STATUS_FILTER_LIST,
  errHandling,
  formatStatus,
} from 'utils';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Text, Title } = Typography;
const { confirm } = Modal;

export function Warehouse() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(0);
  const [defaultWarehouse, setDefaultWarehouse] = useState('');
  const [loadingButton, setLoadingButton] = useState({open: false, id: null});
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState('');
  const [warehouseList, setWarehouseList] = useState([]);

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const buttonAction = (row = {}) => {
    const { id = '', status = '', primary = '' } = row;
    return (
      <div className="button-table">
        <Row>
          {!primary ? (
            <Col span={12} className="button">
              {status === 'active' ? (
                <PdButton
                  className="height-32"
                  type="danger"
                  outline
                  full
                  onClick={handleDeactivateWarehouse.bind(this, id)}>
                  Deactivate
                </PdButton>
              ) : (
                <PdButton
                  className="height-32"
                  type="primary"
                  outline
                  full
                  onClick={handleActivateWarehouse.bind(this, id)}>
                  Reactivate
                </PdButton>
              )}
            </Col>
          ) : (
            <Col lg={12} />
          )}

          <Col span={12} className="button">
            {status === 'active' ? (
              <Link
                to={{
                  pathname: '/warehouses/edit',
                  search: `id=${id}`,
                  data: { admin: row },
                }}>
                <PdButton className="height-32" type="primary" full>
                  Edit
                </PdButton>
              </Link>
            ) : (
              <PdButton
                className="height-32"
                type="danger"
                full
                onClick={handleDeleteWarehouse.bind(this, id)}>
                Delete
              </PdButton>
            )}
          </Col>
        </Row>
      </div>
    );
  };

  const warehouseStatus = (row) => (
    <div className="status-indicator">
      <span className={row.status !== 'inactive' ? 'active' : 'hidden'}>
        {displayStatus(row.status)}
      </span>
    </div>
  );

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      width: '10%',
      center: true,
    },
    {
      name: 'Warehouse',
      selector: row => row.title,
      width: '25%',
      center: true,
    },
    {
      name: 'Acumatica ID',
      selector: row => row.acumatica_id,
      width: '20%',
      center: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      width: '20%',
      cell: warehouseStatus,
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  function handleDeactivateWarehouse(warehouseId) {
    confirm({
      title: 'Are you sure deactivate this warehouse?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await WarehouseServices.deactivateWarehouse(warehouseId);
          fetchWarehouse();
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  async function handleActivateWarehouse(warehouseId) {
    setLoadStatus(true);
    try {
      await WarehouseServices.activateWarehouse(warehouseId);
      fetchWarehouse();
      message.success('Successfully restore the warehouse');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function handleDeleteWarehouse(warehouseId) {
    confirm({
      title: 'Are you sure remove this warehouse?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await WarehouseServices.deleteWarehouse(warehouseId);
          fetchWarehouse();
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  async function fetchWarehouse({ page, status, q } = {}) {
    setLoadStatus(true);
    try {
      const { data } = await WarehouseServices.getWarehouseList({
        cursor: page,
        status,
        q,
      });
      const warehouseData = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setWarehouseList(warehouseData);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function fetchDefaultWarehouse() {
    setLoadStatus(true);
    try {
      const {
        data: { data = {} },
      } = await WarehouseServices.getDefaultWarehouse();
      const { title = '' } = data;
      setDefaultWarehouse(title);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/warehouses',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, ['limit', 'page', 'status', 'q'])
    );
    navigate({
      pathname: '/warehouses',
      search: stringified,
    });
  }

  function filterWarehouse(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/warehouses',
      search: stringified,
    });
  }

  async function handleApplyWarehouse(warehouseId) {
    setLoadingButton({open: true, id: warehouseId});
    try {
      await WarehouseServices.setPrimaryWarehouse(warehouseId);
      fetchDefaultWarehouse();
      message.success('Successfully apply default warehouse');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingButton({open: false, id: null});
      setShowModal(false);
    }
  }

  useEffect(() => {
    fetchProfile();
    fetchDefaultWarehouse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/warehouses',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    if (parsed['status']) setStatus(parsed['status']);
    else setStatus('');

    fetchWarehouse({
      page: parsed['page'],
      q: parsed['q'],
      status: parsed['status'],
    });
  }, [parsed['page'], parsed['status'], parsed['q'], defaultWarehouse]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-warehouse">
      <div className="content">
        <div className="pageTitle">Warehouse</div>
        <Content className="pageContent">
          <Row gutter={[18, 36]} align={'middle'}>
            <Col className="mt-1" lg={7}>
              <Search
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterButton
                className="search-product"
                placeholder="Search Warehouse"
                onSearch={filterWarehouse.bind(this, 'q')}
              />
            </Col>
            <Col lg={3} className="mt-1">
              <Select
                className="w-full"
                onChange={filterWarehouse.bind(this, 'status')}
                value={status}>
                {ACTIVE_INACTIVE_STATUS_FILTER_LIST.map((status, i) =>
                  !status ? (
                    <Option key={i} value={status} selected hidden>
                      Status
                    </Option>
                  ) : (
                    <Option key={i} value={status}>
                      {formatStatus(status)}
                    </Option>
                  )
                )}
              </Select>
            </Col>
            <Col className="mt-1" lg={4}>
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>
            <Col lg={{ span: 4, offset: 5 }}>
              <Link
                to={{
                  pathname: `/warehouses/create`,
                  state: { prevPath: '/warehouses', parsed },
                }}>
                <PdButton className="height-32" type="primary" full outline>
                  Add New Warehouse
                </PdButton>
              </Link>
            </Col>
          </Row>
          <Divider  style={{borderColor : '#AAAAAA'}} />
          <Row gutter={[18, 42]} align={'middle'} style={{border : '1px solid #AAAAAA', margin : '40px 0 24px 0' ,padding : '24px', borderRadius : '10px'}}>
            <Col lg={7}>
              <Title style={{margin : 0}} level={5}>Default Warehouse</Title>
              <Text level={4}>{defaultWarehouse}</Text>
            </Col>
            <Col lg={7}>
            </Col>
            <Col lg={{ span: 5, offset:5 }}>
              <PdButton
                className="height-32"
                type="danger"
                full
                outline
                onClick={setShowModal.bind(this, true)}>
                Change
              </PdButton>
            </Col>
          </Row>
          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={warehouseList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
      {/* Modal Section */}
      {showModal && (
        <Modal
          title="Default Warehouse"
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={setShowModal.bind(this, false)}
          footer={false}>
          <Row gutter={18} align={'middle'}>
            {warehouseList.map(
              ({ id = '', status = '', title = '' }) =>
                status === 'active' && (
                  <Col lg={24} className="modal-warehouse-item" key={id}>
                    <Col lg={19}>
                      <Text>{title}</Text>
                    </Col>
                    <Col lg={5}>
                      <PdButton
                        className="height-32"
                        type="danger"
                        full
                        loading={loadingButton.open && loadingButton.id === id }
                        onClick={handleApplyWarehouse.bind(this, id)}>
                        Apply
                      </PdButton>
                    </Col>
                  </Col>
                )
            )}
          </Row>
        </Modal>
      )}
    </Layout>
  );
}

export default { Warehouse };
